import { parseSafeReturn } from "../lib/safeReturn";
import { createProductAnalyticsEvent } from "./eventFactory";
import type { UtmFields } from "./eventTypes";

export const TRANSITION_TTL_MS = 30 * 60 * 1_000;
const STORAGE_KEY = "pp_product_transition_v1";
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const UTM_KEYS = ["source", "medium", "campaign", "content", "term"] as const;
const UTM_VALUE = /^[A-Za-z0-9._-]{1,100}$/;

export type TransitionEnvelope = {
  transitionId: string;
  returnTo: string | null;
  landingPath: string;
  utm: UtmFields;
  consentState: "granted";
  createdAt: number;
  attributionClientEventId: string;
  attributionOccurredAt?: string;
};

function stripAcquisition(url: URL) {
  const clean = new URL(url.href);
  clean.searchParams.delete("transition_id");
  clean.searchParams.delete("returnTo");
  for (const key of UTM_KEYS) clean.searchParams.delete(`utm_${key}`);
  return `${clean.pathname}${clean.search}${clean.hash}`;
}

export function captureTransitionEnvelope(
  url: URL,
  options: { now?: number; replace?: (path: string) => void } = {},
): TransitionEnvelope | null {
  const transitionId = url.searchParams.get("transition_id");
  if (!transitionId) return null;
  (options.replace ?? ((path) => history.replaceState(null, "", path)))(stripAcquisition(url));
  const returnCandidate = url.searchParams.get("returnTo");
  const returnTo = returnCandidate === null ? null : parseSafeReturn(returnCandidate);
  if (!UUID_V4.test(transitionId) || (returnCandidate !== null && !returnTo)) return null;
  const utm: UtmFields = {};
  for (const key of UTM_KEYS) {
    const value = url.searchParams.get(`utm_${key}`);
    if (value && UTM_VALUE.test(value)) utm[key] = value;
  }
  const envelope: TransitionEnvelope = {
    transitionId, returnTo, landingPath: url.pathname, utm, consentState: "granted",
    createdAt: options.now ?? Date.now(), attributionClientEventId: crypto.randomUUID(),
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    return null;
  }
  return envelope;
}

function readTransitionEnvelope(now: number, remove: boolean): TransitionEnvelope | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
    if (remove) sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const envelope = JSON.parse(raw) as TransitionEnvelope;
    const validLanding = typeof envelope.landingPath === "string" && /^\/(?!\/)(?!.*[?#])[A-Za-z0-9_/:.*-]{0,239}$/.test(envelope.landingPath);
    const validReturn = envelope.returnTo === null || parseSafeReturn(envelope.returnTo) === envelope.returnTo;
    const validUtm = envelope.utm && typeof envelope.utm === "object"
      && Object.keys(envelope.utm).every((key) => UTM_KEYS.includes(key as typeof UTM_KEYS[number]))
      && Object.values(envelope.utm).every((value) => typeof value === "string" && UTM_VALUE.test(value));
    const validAttributionTime = envelope.attributionOccurredAt === undefined
      || (!Number.isNaN(Date.parse(envelope.attributionOccurredAt)) && Date.parse(envelope.attributionOccurredAt) >= envelope.createdAt);
    if (!UUID_V4.test(envelope.transitionId) || !UUID_V4.test(envelope.attributionClientEventId)
      || envelope.consentState !== "granted" || !validLanding || !validReturn || !validUtm || !validAttributionTime
      || typeof envelope.createdAt !== "number" || now < envelope.createdAt
      || now - envelope.createdAt > TRANSITION_TTL_MS) {
      if (!remove) sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return envelope;
  } catch {
    if (!remove) {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* unavailable storage remains fail closed */ }
    }
    return null;
  }
}

export function consumeTransitionEnvelope(now = Date.now()): TransitionEnvelope | null {
  return readTransitionEnvelope(now, true);
}

export function peekTransitionEnvelope(now = Date.now()): TransitionEnvelope | null {
  return readTransitionEnvelope(now, false);
}

export function acknowledgeTransitionEnvelope(transitionId: string) {
  const envelope = peekTransitionEnvelope();
  if (envelope?.transitionId !== transitionId) return false;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

/** Call only after authentication is positively confirmed; removal happens before event creation. */
export function consumeAcquisitionAttribution(artifactSha: string, now = Date.now()) {
  const envelope = consumeTransitionEnvelope(now);
  if (!envelope) return null;
  return createProductAnalyticsEvent("acquisition_attributed", {
    transitionId: envelope.transitionId,
    landingPath: envelope.landingPath,
    utm: envelope.utm,
    consentState: "granted",
  }, { artifactSha, now: () => new Date(now) });
}

export function peekAcquisitionAttribution(artifactSha: string, now = Date.now()) {
  let envelope = peekTransitionEnvelope(now);
  if (!envelope) return null;
  if (!envelope.attributionOccurredAt) {
    envelope = { ...envelope, attributionOccurredAt: new Date(now).toISOString() };
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(envelope)); } catch { return null; }
  }
  return createProductAnalyticsEvent("acquisition_attributed", {
    transitionId: envelope.transitionId,
    landingPath: envelope.landingPath,
    utm: envelope.utm,
    consentState: "granted",
  }, {
    artifactSha,
    now: () => new Date(envelope.attributionOccurredAt!),
    uuid: () => envelope.attributionClientEventId as `${string}-${string}-${string}-${string}-${string}`,
  });
}
